import { createTheme } from "@mui/material";

/**
 * Design tokens transcribed from the approved Raw Surface Analysis mockup.
 *
 * The mockup supports both a light and a dark palette (the light one is what got transcribed
 * into the plan's Design system section, and is what this file originally used). Dark is the one
 * actually in use throughout - it's what renders for anyone whose system/browser prefers dark
 * mode, which is the environment this was reviewed and approved in. DataVisualizer has no
 * light/dark toggle, so rather than support both, this file just *is* the dark palette.
 *
 * Kept as plain exports as well as a MUI theme because the chart draws on a canvas and cannot
 * read MUI's palette - RawSurfaceChart imports these directly so the plotted colours and the
 * surrounding UI cannot drift apart.
 */
export const tokens = {
    ink: "rgb(20,18,19)",
    brand: "rgb(237,28,36)",
    brandDim: "rgb(200,24,31)",
    paper: "rgb(22,20,21)",
    surface: "rgb(32,29,30)",
    surface2: "rgb(38,35,36)",
    line: "rgb(58,54,55)",
    muted: "rgb(158,155,156)",
    muted2: "rgb(120,117,118)",
    text: "rgb(236,234,234)",
    textSoft: "rgb(190,187,188)",

    // Data colours. Deliberately not brand red for rejects: red is the brand accent and is
    // already used for primary actions, so reusing it for data would conflate "RedAlign" with
    // "bad". Teal/burnt-orange also separate better for the most common colour-vision deficiencies.
    kept: "rgb(103,166,174)",
    keptFill: "rgba(103,166,174,0.16)",
    reject: "rgb(224,132,96)",
    rejectFill: "rgba(224,132,96,0.18)",
    warn: "rgb(224,181,90)",

    // The mockup's *muted* red/green pair, shared by Scan Analysis's charts and Raw Surface's
    // Machine/Motor range bands - deliberately separate from kept/reject above, since this pair
    // encodes which shaft something belongs to, not whether it was accepted.
    machine: "rgb(196,60,52)",
    motor: "rgb(52,142,95)",

    shadow: "0 1px 2px rgba(0,0,0,0.3), 0 8px 24px rgba(0,0,0,0.35)",
    radius: 10,
    radiusControl: 6,
};

const montserrat = '"Montserrat", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
/** For non-MUI consumers (e.g. Chart.js, which can't read the theme's typography config directly). */
export const montserratFont = montserrat;
const systemStack = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';

/** Monospace, tabular figures - so columns of numbers line up and do not jitter as values change. */
export const monoFont = 'ui-monospace, "SF Mono", "JetBrains Mono", Menlo, Consolas, monospace';

export const numberSx = {
    fontFamily: monoFont,
    fontVariantNumeric: "tabular-nums",
};

/** Uppercase section heading used on every rail card in the mockup. */
export const cardTitleSx = {
    fontSize: "12.5px",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    color: tokens.textSoft,
};

export default createTheme({
    palette: {
        // Without this, MUI still computes disabled-state opacity, hover/focus overlays and
        // elevation tinting assuming a light surface, even with every colour above overridden.
        mode: "dark",
        primary: { main: tokens.ink },
        secondary: { main: tokens.brand },
        background: { default: tokens.paper, paper: tokens.surface },
        text: { primary: tokens.text, secondary: tokens.muted },
        divider: tokens.line,
        success: { main: tokens.kept },
        warning: { main: tokens.warn },
        error: { main: tokens.reject },
        // No blue anywhere in the mockup's palette - an unstyled MUI info Alert falls back to
        // stock Material blue, which reads as "not RedAlign" next to the rest of the app. Muted
        // grey keeps it inside the existing neutral palette instead of introducing a new hue.
        info: { main: tokens.muted },
    },
    typography: {
        // Body copy stays on the system stack for legibility at small sizes; Montserrat is
        // reserved for headings and controls, matching the mockup.
        fontFamily: systemStack,
        fontSize: 14,
        h1: { fontFamily: montserrat },
        h2: { fontFamily: montserrat },
        h3: { fontFamily: montserrat },
        h4: { fontFamily: montserrat },
        h5: { fontFamily: montserrat },
        h6: { fontFamily: montserrat },
        button: { fontFamily: montserrat, fontWeight: 600, fontSize: "13px", textTransform: "none" },
    },
    components: {
        MuiInputLabel: {
            styleOverrides: {
                root: {
                    // MUI colours a focused outlined label with palette.primary.main, which here is
                    // ink (near-black) because ink is the app's chrome colour. On the dark surface
                    // that makes the label all but disappear the moment the field is focused - the
                    // unfocused state is fine, since that uses text.secondary. Use the normal text
                    // colour for focus instead, which is what the mockup shows.
                    "&.Mui-focused": { color: tokens.text },
                },
            },
        },
        MuiButton: {
            // The mockup's dominant button is brand red (`.btn-primary`) - ink is chrome-only,
            // never a button fill. Defaulting unstyled buttons to "secondary" (brand) here means
            // a component only has to opt OUT (color="inherit") for the minority of buttons that
            // are deliberately neutral - Cancel, Reset-to-defaults, a preview action sitting next
            // to a persisting one - rather than every primary action having to opt in.
            defaultProps: { color: "secondary" },
            styleOverrides: {
                root: {
                    borderRadius: tokens.radiusControl,
                    padding: "8px 16px",
                    boxShadow: "none",
                    "&:hover": { boxShadow: "none" },
                },
                containedSecondary: {
                    backgroundColor: tokens.brand,
                    "&:hover": { backgroundColor: tokens.brandDim },
                },
                // Matches the mockup's .btn-ghost { border-color: var(--line) }. Unstyled,
                // MUI derives an outlined + color="inherit" button's border from the current
                // text colour - white in this dark theme - which reads as too bright next to
                // every other border in the app. Applies to every ghost-style button at once
                // (Apply, Cancel, Reset to defaults, ...), not just one call site.
                outlinedInherit: {
                    borderColor: tokens.line,
                    "&:hover": {
                        borderColor: tokens.line,
                        backgroundColor: tokens.surface2,
                    },
                },
            },
        },
        MuiSwitch: {
            styleOverrides: {
                switchBase: {
                    "&.Mui-checked": { color: tokens.kept },
                    "&.Mui-checked + .MuiSwitch-track": { backgroundColor: tokens.kept, opacity: 0.5 },
                },
            },
        },
        MuiCard: {
            defaultProps: { elevation: 0 },
            styleOverrides: {
                root: {
                    backgroundColor: tokens.surface,
                    border: `1px solid ${tokens.line}`,
                    borderRadius: tokens.radius,
                    boxShadow: tokens.shadow,
                },
            },
        },
        MuiCardContent: {
            styleOverrides: {
                root: {
                    fontSize: "small",
                    padding: "14px",
                    "&:last-child": { paddingBottom: "14px" },
                },
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    backgroundColor: tokens.surface2,
                    borderRadius: tokens.radiusControl,
                    fontSize: "13px",
                },
            },
        },
        MuiTabs: {
            styleOverrides: {
                // The tab strip sits directly under the header on the same dark ink field, so the
                // indicator is the brand red underline rather than MUI's default primary.
                root: { backgroundColor: tokens.ink, minHeight: "40px", padding: "0 16px" },
                indicator: { backgroundColor: tokens.brand, height: "2px" },
            },
        },
        MuiTab: {
            styleOverrides: {
                root: {
                    fontFamily: montserrat,
                    fontSize: "13px",
                    fontWeight: 600,
                    letterSpacing: "0.01em",
                    textTransform: "none",
                    minHeight: "40px",
                    padding: "12px 16px 11px",
                    color: "rgba(255,255,255,0.55)",
                    "&:hover": { color: "rgba(255,255,255,0.85)" },
                    "&.Mui-selected": { color: "#fff" },
                },
            },
        },
        MuiSlider: {
            styleOverrides: {
                root: { color: tokens.kept },
                valueLabel: { backgroundColor: tokens.ink },
            },
        },
        MuiAlert: {
            styleOverrides: { root: { borderRadius: tokens.radiusControl, fontSize: "13px" } },
        },
        MuiTooltip: {
            styleOverrides: {
                tooltip: {
                    backgroundColor: tokens.ink,
                    color: tokens.text,
                    fontSize: "12px",
                    lineHeight: 1.5,
                    borderRadius: tokens.radiusControl,
                    padding: "6px 9px",
                    boxShadow: tokens.shadow,
                },
                arrow: { color: tokens.ink },
            },
        },
        MuiDialog: {
            styleOverrides: { paper: { borderRadius: tokens.radius } },
        },
    },
});
